import { useReducer, useEffect, useCallback, useRef } from 'react';
import _ from 'lodash';
import { toast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import useScorm from './useScorm';
import { getOverallScore } from '../selectors/overalScoreSelector';

function useGlobal() {
  const {
    setCompleted,
    setFailed,
    setPassed,
    setSuspendData,
    setLessonLocation,
    setScore,
    exitScorm,
  } = useScorm();
  const localLanguage = useRef();
  const debouncedSetSuspendData = useRef();

  function reducer(state, action) {
    switch (action.type) {
      case 'SET_COURSE':
        const course = action.payload;
        return {
          ...state,
          course: action.payload,
          player: {
            ...state.player,
            maxPage: course?.pages?.length || 1,
          },
        };
      case 'NEXT_PAGE':
        return {
          ...state,
          player: {
            ...state.player,
            curPage:
              state.player.curPage < state.player.maxPage
                ? state.player.curPage + 1
                : state.player.curPage,
          },
        };
      case 'PREV_PAGE':
        return {
          ...state,
          player: {
            ...state.player,
            curPage:
              state.player.curPage > 1
                ? state.player.curPage - 1
                : state.player.curPage,
          },
        };
      case 'GO_PAGE':
        return {
          ...state,
          player: {
            ...state.player,
            curPage: action.payload,
          },
        };
      case 'TOGGLE_MENU':
        return {
          ...state,
          player: {
            ...state.player,
            showMenu: !state.player.showMenu,
          },
        };
      case 'RESTORE_TRACKING':
        return {
          ...state,
          tracking: action.payload,
        };
      case 'PATCH_TRACKING':
        const { resourceId, data } = action.payload;
        const updatedState = {
          ...state,
          tracking: {
            ...state.tracking,
            [resourceId]: {
              ...state.tracking[resourceId],
              ...data,
            },
          },
        };
        saveTrackingAsSuspendData(updatedState);
        return updatedState;
      case 'CLEAR_TRACKING':
        const updatedState2 = {
          ...state,
          tracking: {
            ...state.tracking,
            [action.payload.resourceId]: {},
          },
        };
        saveTrackingAsSuspendData(updatedState2);
        return updatedState2;
      case 'SET_MESSAGE':
        return {
          ...state,
          player: {
            ...state.player,
            messages: {
              ...state.player.messages,
              [action.payload.key]: action.payload.value,
            },
          },
        };
      case 'SET_SCORM_READY':
        return {
          ...state,
          scorm: {
            ...state.scorm,
            scormReady: action.payload,
          },
        };
      case 'SET_SCORM_INITIAL_DATA':
        return {
          ...state,
          scorm: {
            ...state.scorm,
            initialScormData: action.payload,
          },
        };
      case 'SET_DISABLE_SCORM':
        return {
          ...state,
          player: {
            ...state.player,
            disableScorm: action.payload,
          },
        };
      case 'SET_EDIT_MODE':
        return {
          ...state,
          player: {
            ...state.player,
            editMode: action.payload,
          },
        };
      case 'SET_TEST_CALLBACK':
        return {
          ...state,
          test: {
            ...state.test,
            callback: action.payload,
          },
        };
      default:
        handleError(new Error(`Action type not recognized: ${action.type}`));
    }
  }

  const initialState = {
    player: {
      showMenu: false,
      curPage: 1,
      maxPage: 1,
      disableScorm: false,
      messages: {
        courseCompleted: 'Course completed',
      },
    },
    tracking: {},
    course: {},
    test: {
      callback: () => {},
    },
    scorm: {
      scormReady: false, // scorm initialized successfuly?
      initialScormData: {}, // payload data after initialize scorm successfully
    },
  };
  const [state, dispatch] = useReducer(reducer, initialState);

  function initCourseData(course) {
    dispatch({ type: 'SET_COURSE', payload: course });
  }

  function toggleMenu() {
    dispatch({ type: 'TOGGLE_MENU' });
  }

  function goNextPage() {
    dispatch({ type: 'NEXT_PAGE' });
  }

  function goPrevPage() {
    dispatch({ type: 'PREV_PAGE' });
  }

  function goPage(idx) {
    dispatch({ type: 'GO_PAGE', payload: idx });
  }

  function getCurrentPage() {
    return _.get(state.course, `pages[${state.player?.curPage - 1}]`) || {};
  }

  const getPages = useCallback(() => {
    return state.course?.pages || [];
  }, [state.course]);

  function getColorScheme() {
    return state.course?.colorScheme || {};
  }

  function getCoursePercentage() {
    return Math.round((getCountPagesCompleted() / state.player?.maxPage) * 100);
  }

  //////////////////
  // useTracking
  //////////////////

  const getTracking = useCallback(
    (resourceId) => {
      return _.get(state.tracking, resourceId) || {};
    },
    [state.tracking]
  );

  function restoreTracking(data) {
    dispatch({ type: 'RESTORE_TRACKING', payload: data });
  }

  function patchTracking(resourceId, data) {
    dispatch({ type: 'PATCH_TRACKING', payload: { resourceId, data } });
  }

  function clearTracking(resourceId) {
    dispatch({ type: 'CLEAR_TRACKING', payload: { resourceId } });
  }

  function setMessage(key, value) {
    dispatch({ type: 'SET_MESSAGE', payload: { key, value } });
  }

  function setMessageCourseCompleted(message) {
    setMessage(
      'courseCompleted',
      message || state.player.messages.courseCompleted
    );
  }

  function onPageStarted() {
    const { resourceId } = getCurrentPage();
    const data = { started: true };
    patchTracking(resourceId, data);
  }

  function onPageCompleted(callback = () => {}) {
    const { resourceId } = getCurrentPage();

    // avoid double calling
    const wasCompletedBefore = getTracking(resourceId)?.completed;
    if (wasCompletedBefore) return;

    const data = { completed: true };
    patchTracking(resourceId, data);

    // must show tooltip to advance?
    callback && typeof callback === 'function' && !isInLastPage() && callback();
  }

  function isInFirstPage() {
    return state.player.curPage === 1;
  }

  function isInLastPage() {
    return state.player.curPage === state.player.maxPage;
  }

  function saveTrackingAsSuspendData(updatedState) {
    // save suspend data (when in scorm)
    try {
      if (!updatedState.player.disableScorm) {
        // using debounce to avoid spamming suspend_data
        if (!debouncedSetSuspendData) return;
        debouncedSetSuspendData.current?.cancel &&
          debouncedSetSuspendData.current.cancel();
        debouncedSetSuspendData.current = _.debounce(
          () => setSuspendData(updatedState.tracking),
          2000 // every 2 secs save suspend_data
        );
        debouncedSetSuspendData.current();
      }
    } catch (err) {
      handleError(err);
    }
  }

  const getCountPagesCompleted = useCallback(() => {
    const pages = getPages();
    let pagesCompleted = 0;
    _.forEach(pages, ({ resourceId }) => {
      const { completed } = getTracking(resourceId);
      completed === true && pagesCompleted++;
    });
    return pagesCompleted;
  }, [getPages, getTracking]);

  const getPagesCompleted = useCallback(() => {
    const pages = getPages();
    const completedPages = [];
    _.forEach(pages, ({ resourceId }, idx) => {
      const { completed } = getTracking(resourceId);
      completed === true && completedPages.push(idx);
    });
    return completedPages;
  }, [getPages, getTracking]);

  function onPageChanged(currentPage) {
    const hasFaceVideo = !!currentPage?.facevideo?.name;
    if (hasFaceVideo && !state.player.showMenu) toggleMenu();
  }

  const isCourseCompleted = useCallback(() => {
    return !!getTracking(state.course.courseId).completed;
  }, [getTracking, state.course.courseId]);

  const onCourseCompleted = useCallback(() => {
    if (isCourseCompleted()) return;
    !state.player.disableScorm &&
      toast.success(state.player.messages.courseCompleted);

    const { courseId, pages } = state.course;
    const data = { completed: true, completedAt: new Date().toISOString() };
    patchTracking(courseId, data);

    // mark lesson status as completed
    if (!state.player.disableScorm) {
      setCompleted(state.test.callback);

      const overallScore = getOverallScore(state.course, state.tracking);
      if (overallScore.completed) {
        overallScore.passed
          ? setPassed(state.test.callback)
          : setFailed(state.test.callback);
      } else {
        // check if has quiz or minigame...
        const hasQuizOrMinigame = !!_.find(
          pages,
          ({ resourceType }) =>
            resourceType === 'quiz' || resourceType === 'game'
        );
        // // if does not have any ot them, also set course aas passed
        if (!hasQuizOrMinigame) setPassed(state.test.callback);
      }
    }
  }, [
    isCourseCompleted,
    setCompleted,
    setPassed,
    setFailed,
    state.course,
    state.tracking,
    state.player.disableScorm,
    state.player.messages.courseCompleted,
    state.test.callback,
  ]);

  function setCourseScore(score) {
    !state.player.disableScorm && setScore(score, state.test.callback);
  }

  function setCoursePassed() {
    !state.player.disableScorm && setPassed(state.test.callback);
  }

  function setCourseFailed() {
    !state.player.disableScorm && setFailed(state.test.callback);
  }

  function exitCourse() {
    !state.player.disableScorm && exitScorm(state.test.callback);
  }

  function setScormReady(ready) {
    dispatch({ type: 'SET_SCORM_READY', payload: ready });
  }

  function setScormInitialData(initialData = {}) {
    dispatch({ type: 'SET_SCORM_INITIAL_DATA', payload: initialData });
  }

  function setDisableScorm(disable) {
    dispatch({ type: 'SET_DISABLE_SCORM', payload: disable });
  }

  function setEditMode(editMode) {
    dispatch({ type: 'SET_EDIT_MODE', payload: editMode });
  }

  function setTestCallback(callbackFunction) {
    dispatch({ type: 'SET_TEST_CALLBACK', payload: callbackFunction });
  }

  function setLocalLanguage(l) {
    localLanguage.current = l;
  }

  const isNavigationModeFree =
    state?.course?.settings?.navigationMode === 'free';

  const showCloseCourseButton =
    state?.course?.settings?.showCloseCourseButton === true;

  // save curPage on lesson location
  useEffect(() => {
    try {
      if (state.player.curPage === 1 || state.player.disableScorm) return;

      setLessonLocation(
        JSON.stringify({
          curPage: state.player.curPage,
          completed: getPagesCompleted(),
        }),
        state.test.callback
      );
    } catch (err) {
      handleError(err);
    }
  }, [
    setLessonLocation,
    state.player.curPage,
    state.player.disableScorm,
    state.test.callback,
    getPagesCompleted,
  ]);

  useEffect(() => {
    if (getCountPagesCompleted() === state.player?.maxPage) {
      onCourseCompleted();
    }
  }, [getCountPagesCompleted, onCourseCompleted, state.player]);

  function handleError(err) {
    console.error(err);
    toast.error(err.message || err);
  }

  return {
    player: state.player,
    scorm: state.scorm,
    course: state.course,
    tracking: state.tracking,
    initCourseData,
    toggleMenu,
    goNextPage,
    goPrevPage,
    goPage,
    getCurrentPage,
    getPages,
    getColorScheme,
    getTracking,
    patchTracking,
    restoreTracking,
    clearTracking,
    setMessageCourseCompleted,
    onPageStarted,
    onPageCompleted,
    getCoursePercentage,
    onPageChanged,
    setDisableScorm,
    setScormReady,
    setScormInitialData,
    setEditMode,
    setTestCallback,
    handleError,
    setCourseScore,
    setCoursePassed,
    setCourseFailed,
    exitCourse,
    isInFirstPage,
    isInLastPage,
    setLocalLanguage,
    courseCompleted: isCourseCompleted(),
    isNavigationModeFree,
    showCloseCourseButton,
  };
}

export default useGlobal;
